import React, { FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import { DatePicker, Select } from '@vwfs-bronson/bronson-react';
import { useFormikContext } from 'formik';
import { FormField } from '../../../components/FormField/FormField';
import {
  charsReplacementMap1,
  getMobilePhoneLengthMaskForCountry,
  MaskedTextFormField,
  masks,
} from '../../../components/FormFields/MaskedTextFormField';
import { SelectFormField } from '../../../components/FormFields/SelectFormField';
import { TextFormField, TextFormFieldProps } from '../../../components/FormFields/TextFormField';
import {
  genderOptions,
  isExistingClient,
  milesCoCustomerRelationOptions,
  professionOptions,
  telephoneCountryCodesOptions,
  yesOrNot,
} from '../../../resources/base/models';
import { normalizeDate } from '../../../services/common/format';
import { getMaxBirthDate, getMinBirthDate } from '../../../services/common/validation';
import { formatEmail } from '../utils/emailFormatter';
import { PrivateCustomerProspectForm } from './initialValues';

export const Email: FunctionComponent = () => {
  const { setFieldValue } = useFormikContext<any>();
  return (
    <TextFormField
      fieldName="email"
      labelTranslationKey="step2:email:label"
      onChange={(event) => {
        setFieldValue('email', formatEmail(event.target.value));
      }}
    />
  );
};

export const IsExistingClient: FunctionComponent = () => {
  return (
    <FormField
      required
      type="select"
      name="isExistingClient"
      render={(fieldProps) => (
        <Select {...fieldProps}>
          {isExistingClient?.map(({ label, value }) => (
            <Select.Item key={`${label}`} value={value}>
              {label}
            </Select.Item>
          ))}
        </Select>
      )}
    />
  );
};

export const PreviousContractNumber: FunctionComponent = () => (
  <TextFormField fieldName="previousContractNumber" labelTranslationKey="step2:previousContractNumber:label" />
);

export const CarToBeReturned: FunctionComponent = () => {
  const { t } = useTranslation();

  return (
    <FormField
      required
      type="select"
      name="carToBeReturned"
      labelText={t('step2:heading:carToBeReturned')}
      render={(fieldProps) => (
        <Select {...fieldProps}>
          {yesOrNot?.map(({ label, value }) => (
            <Select.Item key={`${label}`} value={`${value}`}>
              {label}
            </Select.Item>
          ))}
        </Select>
      )}
    />
  );
};

export const Gender: FunctionComponent<{ disabled?: boolean }> = ({ disabled }) => {
  return (
    <SelectFormField
      fieldName="gender"
      labelTranslationKey="step2:gender:label"
      dropDownOptionsList={genderOptions}
      isDisabled={disabled}
    />
  );
};

export const FirstName: FunctionComponent<Partial<TextFormFieldProps>> = (props) => (
  <MaskedTextFormField
    {...props}
    mask={masks.nameMask}
    fieldName="firstName"
    labelTranslationKey="step2:firstName:label"
    overwrite
    onAcceptCharsReplacementMap={charsReplacementMap1}
  />
);

export const LastName: FunctionComponent<Partial<TextFormFieldProps>> = (props) => (
  <MaskedTextFormField
    {...props}
    mask={masks.nameMask}
    fieldName="lastName"
    labelTranslationKey="step2:lastName:label"
    overwrite
    onAcceptCharsReplacementMap={charsReplacementMap1}
  />
);

export const BirthName: FunctionComponent<Partial<TextFormFieldProps>> = (props) => (
  <MaskedTextFormField
    {...props}
    mask={masks.nameMask}
    fieldName="birthName"
    labelTranslationKey="step2:birthName:label"
    overwrite
    onAcceptCharsReplacementMap={charsReplacementMap1}
  />
);

interface DateOfBirthProps {
  labelText: string;
  infoText: string;
  disabled?: boolean;
}

export const DateOfBirth: FunctionComponent<DateOfBirthProps> = ({ labelText, infoText, disabled }) => {
  const { setFieldValue, values } = useFormikContext<any>();
  return (
    <FormField
      name="dateOfBirth"
      type="other"
      labelText={labelText}
      required
      infoText={infoText}
      render={(fieldProps) => (
        <DatePicker
          {...fieldProps}
          maxDate={getMaxBirthDate()}
          minDate={getMinBirthDate()}
          dateFormat="d/m/Y"
          locale="fr"
          placeholder="j/m/a"
          mode="single"
          disabled={disabled}
          flatpickrOptions={{
            onClose: (e: any) => {
              const date = normalizeDate(e);
              try {
                if (date === 'Invalid date') {
                  throw new Error('');
                }
                setFieldValue(fieldProps.name, normalizeDate(e));
              } catch (error) {
                setFieldValue(fieldProps.name, '');
              }
            },
          }}
          value={values[fieldProps.name]}
          onChange={(e) => {
            setFieldValue(fieldProps.name, normalizeDate(e));
            // setFieldValue(fieldProps.name, e);
          }}
          onBlur={(e) => {
            // setFieldValue(fieldProps.name, e);
            setFieldValue(fieldProps.name, normalizeDate(e));
          }}
        />
      )}
    />
  );
};

export const Profession: FunctionComponent<{ disabled?: boolean }> = ({ disabled }) => {
  return (
    <SelectFormField
      fieldName="profession"
      labelTranslationKey="step2:profession:label"
      dropDownOptionsList={professionOptions}
      isDisabled={disabled}
    />
  );
};

export const CustomerRelations: FunctionComponent = () => {
  return (
    <SelectFormField
      fieldName="customerRelations"
      labelTranslationKey="step2:customerRelations:label"
      dropDownOptionsList={milesCoCustomerRelationOptions}
    />
  );
};

export const TelephonePhone: FunctionComponent = () => {
  return (
    <MaskedTextFormField
      fieldName="telephonePhone"
      labelTranslationKey="step2:telephonePhone:label"
      mask={masks.phonePrefixContactMask}
      placeholder="+33 ##########"
      optional
    />
  );
};

export const MobilePhone: FunctionComponent = () => {
  const { values } = useFormikContext<PrivateCustomerProspectForm>();
  const mask = getMobilePhoneLengthMaskForCountry(values.mobilePhoneCountryCode);
  const isFrenchMobilePhone = values.mobilePhoneCountryCode === '+33';

  return (
    <MaskedTextFormField
      fieldName="mobilePhone"
      labelTranslationKey="step2:mobilePhone:label"
      mask={mask}
      placeholder="##########"
      infoTextTranslationKey={isFrenchMobilePhone ? 'step2:mobilePhone:infoText' : undefined}
    />
  );
};

export const CountryCodeMobilePhone: FunctionComponent = () => {
  const { t } = useTranslation();

  return (
    <FormField
      className="u-text-left"
      name="mobilePhoneCountryCode"
      type="select"
      labelText={t('sf-sac:continueLater:form:customerPhoneCountryCode')}
      required
      render={(fieldProps) => (
        <Select {...fieldProps}>
          {telephoneCountryCodesOptions?.map(({ label, value }) => (
            <Select.Item key={`${label}`} value={`${value}`}>
              {label}
            </Select.Item>
          ))}
        </Select>
      )}
    />
  );
};
